import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

export default function MFASetup({ onComplete }) {
  const [step, setStep] = useState('loading') // loading | enroll | verify | done
  const [qrCode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [factorId, setFactorId] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [factors, setFactors] = useState([])

  useEffect(() => {
    checkMFAStatus()
  }, [])

  async function checkMFAStatus() {
    const { data } = await supabase.auth.mfa.listFactors()
    const verified = data?.totp?.filter(f => f.status === 'verified') || []
    setFactors(verified)
    setStep(verified.length > 0 ? 'done' : 'enroll')
  }

  async function startEnrollment() {
    setLoading(true); setError('')
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: 'ClearFile Authenticator'
    })
    if (error) { setError(error.message); setLoading(false); return }
    setQrCode(data.totp.qr_code)
    setSecret(data.totp.secret)
    setFactorId(data.id)
    setStep('verify')
    setLoading(false)
  }

  async function verifyEnrollment() {
    if (code.length !== 6) { setError('Please enter a 6-digit code'); return }
    setLoading(true); setError('')
    const { data: challenge } = await supabase.auth.mfa.challenge({ factorId })
    const { error } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challenge.id,
      code
    })
    if (error) { setError('Invalid code. Please try again.'); setLoading(false); return }
    setStep('done')
    setLoading(false)
    checkMFAStatus()
    if (onComplete) onComplete()
  }

  async function removeFactor(factorId) {
    if (!window.confirm('Remove 2FA from your account? This will make your account less secure.')) return
    await supabase.auth.mfa.unenroll({ factorId })
    checkMFAStatus()
  }

  if (step === 'loading') return <div className="skeleton" style={{ height: 120, borderRadius: 8 }} />

  if (step === 'done') return (
    <div>
      <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: 10, padding: '14px 18px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 24 }}>🔐</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#065F46' }}>Two-Factor Authentication is Active</div>
          <div style={{ fontSize: 12, color: '#62615D', marginTop: 2 }}>Your account is protected with an authenticator app.</div>
        </div>
      </div>
      {factors.map(f => (
        <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#F7F3EB', borderRadius: 8, padding: '10px 14px' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{f.friendly_name || 'Authenticator App'}</div>
            <div style={{ fontSize: 11, color: '#62615D' }}>Added {new Date(f.created_at).toLocaleDateString()}</div>
          </div>
          <button className="btn btn-ghost" style={{ fontSize: 11, color: '#EF4444' }} onClick={() => removeFactor(f.id)}>Remove</button>
        </div>
      ))}
    </div>
  )

  if (step === 'enroll') return (
    <div>
      <div style={{ background: '#F1E8D7', border: '1px solid #D8C18F', borderRadius: 10, padding: '14px 18px', marginBottom: 16, fontSize: 13, color: '#765B2C', lineHeight: 1.6 }}>
        🔒 Add an extra layer of security to your firm account. You'll need an authenticator app like <strong>Google Authenticator</strong> or <strong>Authy</strong>.
      </div>
      <button className="btn btn-primary" onClick={startEnrollment} disabled={loading} style={{ width: '100%', justifyContent: 'center' }}>
        {loading ? 'Setting up…' : '🔐 Set Up Two-Factor Authentication'}
      </button>
    </div>
  )

  if (step === 'verify') return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: '#62615D', marginBottom: 12 }}>
          Scan this QR code with your authenticator app:
        </div>
        <div style={{ display: 'inline-block', background: '#fff', padding: 12, border: '2px solid #DED6CA', borderRadius: 12 }}>
          <img src={qrCode} alt="QR Code" style={{ width: 160, height: 160, display: 'block' }} />
        </div>
        <div style={{ marginTop: 10, fontSize: 11, color: '#62615D' }}>
          Can't scan? Enter this code manually:<br />
          <code style={{ background: '#F7F3EB', padding: '3px 8px', borderRadius: 4, fontSize: 12, fontWeight: 700, letterSpacing: 2, color: '#202326' }}>{secret}</code>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label className="label">Enter the 6-digit code from your app</label>
        <input
          className="input"
          placeholder="000000"
          value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          style={{ textAlign: 'center', fontSize: 22, letterSpacing: 8, fontFamily: 'monospace' }}
          maxLength={6}
          autoFocus
        />
      </div>

      {error && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn btn-ghost" onClick={() => setStep('enroll')} style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
        <button className="btn btn-primary" onClick={verifyEnrollment} disabled={loading || code.length !== 6} style={{ flex: 2, justifyContent: 'center' }}>
          {loading ? 'Verifying…' : '✓ Enable 2FA'}
        </button>
      </div>
    </div>
  )
}
